/**
 * AudioNotesInput — textarea with optional audio recording for voice notes.
 *
 * Records audio via the browser MediaRecorder, transcribes in the background
 * via /copilot/transcribe (OpenAI Whisper), and appends the transcript to the
 * textarea. The user is never blocked waiting for transcription. The
 * record → transcribe → append machinery lives in hooks/useVoiceDictation.ts,
 * shared with the Merlin's-questions answer box on /roadmap.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  appendTranscript,
  formatDictationDuration,
  useVoiceDictation,
} from '../../hooks/useVoiceDictation';
import styles from './AudioNotesInput.module.css';

interface AudioNotesInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Label above the field. Defaults to the optional-notes wording; override it
   * where the note is REQUIRED, so the caller's own requirement text and this
   * label cannot contradict each other.
   */
  label?: string;
  rows?: number;
}

const AudioNotesInput: React.FC<AudioNotesInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Any thoughts on this notification or recommendation...',
  label = 'Additional notes (optional)',
  rows = 2,
}) => {
  // Keep a ref to the latest value so the async transcription callback always
  // appends to the most recent text, not a stale closure capture.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const onTranscript = useCallback(
    (transcript: string) => onChange(appendTranscript(valueRef.current, transcript)),
    [onChange],
  );

  const {
    isSupported,
    isRecording,
    requestingPermission,
    transcribing,
    duration,
    error,
    toggle,
  } = useVoiceDictation({ onTranscript });

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {isSupported && (
          <button
            type="button"
            className={`${styles.micBtn} ${isRecording ? styles.micBtnRecording : ''}`}
            onClick={toggle}
            disabled={disabled || transcribing || requestingPermission}
            title={isRecording ? 'Stop recording' : 'Record voice note'}
          >
            {isRecording ? (
              /* Stop icon */
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            ) : (
              /* Mic icon */
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Recording indicator */}
      {isRecording && (
        <div className={styles.recordingBar}>
          <span className={styles.recordingDot} />
          <span className={styles.recordingTime}>{formatDictationDuration(duration)}</span>
          <span className={styles.recordingLabel}>Recording...</span>
        </div>
      )}

      {/* Transcribing indicator */}
      {transcribing && (
        <div className={styles.transcribingBar}>
          <span className={styles.spinner} />
          <span className={styles.transcribingLabel}>Transcribing in background...</span>
        </div>
      )}

      <textarea
        className={styles.textarea}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
      />

      {/* Errors */}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default AudioNotesInput;
